<script lang="ts" setup>
import { showLoadingToast, showSuccessToast } from "vant";
import { useGlobalUser } from "@/stores/globalUser";
import { computed, onBeforeMount, ref } from "vue";
import { useI18n } from "vue-i18n";
import { NORMAL_TICKETS_PER_GOLD_TICKET, useCoin } from "@/stores/coin";
import { useRouter } from "vue-router";
import { useMate } from "@/stores/mate";
import { countWords } from "@homegrown/word-counter";
import {
  VoteTargetEntityType,
  VoteTargetType,
} from "@/composable/rank/rankConfig";
import { useCoinRank } from "@/stores/coinRank";

const props = defineProps<{
  type: VoteTargetEntityType;
  item: VoteTargetType;
  loading: boolean;
}>();
const emit = defineEmits<{
  (e: "onVoted"): void;
}>();

// Constants & Stores
const user = useGlobalUser();
const mateStore = useMate();
const router = useRouter();
const coinStore = useCoin();
const { t } = useI18n();
const coinRank = useCoinRank();
// States
const ticketCount = ref(1);
const voteMessage = ref("");
const restGoldTickets = computed(
  () => user.goldenTicketAmount - ticketCount.value
);
const wordsCount = computed(() => countWords(voteMessage.value));
const isVoteMessageValid = computed(() => {
  return wordsCount.value > 200;
});
const prevRankVoteCount = ref(0);
const normalTicketPerGoldTicket = computed(() => {
  return Math.floor(prevRankVoteCount.value * 0.03);
});
const voteErrorMsg = computed(() => {
  if (restGoldTickets.value < 0) {
    return t("Coin.insufficientGoldTickets");
  }
  return "";
});

const isValidToVote = computed(() => {
  return !(
    props.loading ||
    !ticketCount.value ||
    restGoldTickets.value < 0 ||
    !isVoteMessageValid.value
  );
});
// Lifecycle
const isFetchingExchangeRate = ref(false);
onBeforeMount(async () => {
  try {
    isFetchingExchangeRate.value = true;
    await coinRank.getOrFetchHistoryCoinRank();
    const currentRankIdx = coinRank.historyCoinRankList.findIndex(
      (rank) => rank.id === coinRank.currentCoinRank!.id
    );
    const prevRank = coinRank.historyCoinRankList[currentRankIdx + 1];
    prevRankVoteCount.value = prevRank.voteCount;
  } finally {
    isFetchingExchangeRate.value = false;
  }
});

// Methods
const onVote = async () => {
  const loading = showLoadingToast("");
  try {
    await coinStore.vote({
      entityType: props.type,
      entityId: props.item.id!,
      amount: Number(ticketCount.value),
      text: voteMessage.value,
      isAnonymous: 0,
      isGolden: 1,
    });
    emit("onVoted");
    loading.close();
    showSuccessToast(t("Coin.voted"));
  } finally {
    loading.close();
  }
};
</script>
<template>
  <div class="flex flex-col gap-2 flex-1">
    <van-field
      v-model="ticketCount"
      :error-message="voteErrorMsg"
      :label="$t('Coin.goldTicket')"
      :placeholder="$t('Coin.inputTicketNum')"
      class="bg-base-100 border rounded"
      type="digit"
    >
      <template #left-icon>
        <div class="h-full flex items-center">
          <i class="iconify mdi--ticket text-xs text-my-yellow" />
        </div>
      </template>
    </van-field>
    <div class="flex flex-col gap-1">
      <div class="relative">
        <van-field
          v-model="voteMessage"
          :placeholder="$t('Coin.leaveMessage')"
          :rows="5"
          class="bg-base-100 border rounded"
          label-align="top"
          type="textarea"
        />
        <div class="absolute right-0 bottom-0 p-1 text-base-content/50">
          {{ wordsCount }}
        </div>
      </div>
      <span v-if="!isVoteMessageValid" class="text-error">
        {{ $t("Coin.goldTicketVoteLimit") }}
      </span>
      <div class="flex justify-between items-center">
        <span class="flex items-center gap-1 text-base-content/50 text-xs">
          <span>{{ $t("Coin.restGoldTicket") }}:</span>
          <span v-if="loading" class="loading loading-xs" />
          <span v-else>{{ user.goldenTicketAmount }}</span>
          <i class="iconify mdi--ticket text-xs text-my-yellow" />
        </span>
        <button
          class="btn btn-xs rounded btn-neutral"
          @click="router.push({ name: 'goldTicketBalance' })"
        >
          {{ $t("Coin.getGoldTicket") }}
        </button>
      </div>
    </div>
  </div>
  <div class="flex flex-col gap-3">
    <div
      class="flex flex-col items-end text-base-content/50 gap-1 text-xs md:text-sm"
    >
      <span class="flex items-center gap-1">
        {{ $t("Coin.consumptionCoins") }}:
        <span class="text-primary">{{ ticketCount }}</span>
        <i class="iconify mdi--ticket text-xs text-my-yellow" />
      </span>
      <span class="flex items-center gap-1">
        <i class="iconify mdi--ticket text-xs" />
        <span>{{ $t("Coin.ticketNum") }}:</span>
        <span
          v-if="isFetchingExchangeRate"
          class="skeleton-loading-bg w-7 h-4 rounded"
        />
        <span v-else class="text-base-content">{{
          ticketCount * normalTicketPerGoldTicket
        }}</span>
      </span>
    </div>
    <div class="flex items-center justify-between gap-4">
      <div></div>
      <div class="flex items-center justify-end gap-4">
        <button
          :disabled="!isValidToVote || isFetchingExchangeRate"
          class="btn btn-sm rounded btn-primary"
          @click="onVote"
        >
          {{ $t("Coin.buyTicketsAndVote") }}
        </button>
      </div>
    </div>
  </div>
</template>
